import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../../core/utils/apiClient';
import { authService } from '../../auth/services/auth.service';
import type { ChatMessage, Conversation } from '../types';

export interface UseChatSessionOptions {
  /** Initial conversation ID to load on mount. */
  initialConversationId?: number | string | null;
  /** When true, parses and exposes connecting/connected/tool_status SSE events plus the elapsed timer. */
  enableToolStatus?: boolean;
  /** When true, loads the sidebar conversation list and refreshes it after each completed message. */
  enableConversationList?: boolean;
  /** Called after the 'done' SSE event with the conversation id. Use this to navigate/refresh. */
  onConversationCreated?: (id: string) => void;
  /** When set, a NEW conversation is scoped to this company so the agent answers only about it. */
  companyId?: number | null;
}

export interface UseChatSessionReturn {
  // State
  messages: ChatMessage[];
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  isLoading: boolean;
  input: string;
  setInput: (v: string) => void;
  conversationId: number | string | null;
  setConversationId: (id: number | string | null) => void;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;

  // Actions
  sendMessage: (overrideText?: string) => Promise<void>;
  retryLastMessage: () => Promise<void>;
  startNewConversation: () => void;
  loadConversation: (id: number | string) => Promise<void>;

  // Optional features
  conversations: Conversation[];
  loadingConversations: boolean;
  toolElapsed: number;

  // Copy
  copiedId: string | null;
  copyText: (id: string, text: string) => void;
}

/**
 * Shared chat session hook. Owns SSE streaming, conversation history, and copy state for all
 * chat surfaces (admin AIChatPanel, partner/user ChatPage). Visual differences live in the
 * consumer components — this hook is purely state + I/O.
 */
export function useChatSession(options: UseChatSessionOptions = {}): UseChatSessionReturn {
  const {
    initialConversationId = null,
    enableToolStatus = false,
    enableConversationList = false,
    onConversationCreated,
    companyId = null,
  } = options;

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<number | string | null>(initialConversationId);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loadingConversations, setLoadingConversations] = useState(enableConversationList);
  const [toolStartTime, setToolStartTime] = useState<number | null>(null);
  const [toolElapsed, setToolElapsed] = useState(0);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Stable refs so the SSE loop can read the latest callbacks without being a dep
  const onConversationCreatedRef = useRef(onConversationCreated);
  useEffect(() => { onConversationCreatedRef.current = onConversationCreated; }, [onConversationCreated]);
  const companyIdRef = useRef(companyId);
  useEffect(() => { companyIdRef.current = companyId; }, [companyId]);

  const copyText = useCallback((id: string, text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  }, []);

  // Tool-elapsed ticker
  useEffect(() => {
    if (toolStartTime === null) {
      setToolElapsed(0);
      return;
    }
    const interval = setInterval(() => {
      setToolElapsed(Math.floor((Date.now() - toolStartTime) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [toolStartTime]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadConversations = useCallback(async () => {
    if (!enableConversationList) return;
    try {
      const res = await api.get('/api/v1/chat/conversations');
      if (res.ok) {
        const data: Conversation[] = await res.json();
        setConversations(data);
      }
    } finally {
      setLoadingConversations(false);
    }
  }, [enableConversationList]);

  const loadConversation = useCallback(async (id: number | string) => {
    const res = await api.get(`/api/v1/chat/conversations/${id}`);
    if (res.ok) {
      const data = await res.json();
      const historyMessages: ChatMessage[] = (data.messages || []).map((m: any, index: number) => ({
        id: m.id != null ? String(m.id) : `hist-${index}-${m.role}`,
        role: m.role as 'user' | 'assistant',
        content: m.content,
        timestamp: new Date(m.created_at || Date.now()),
        status: 'success' as const,
      }));
      setMessages(historyMessages);
    }
  }, []);

  // Initial sidebar load
  useEffect(() => {
    if (enableConversationList) {
      loadConversations();
    }
  }, [enableConversationList, loadConversations]);

  // Load initial conversation (when set via prop or sidebar click)
  useEffect(() => {
    if (conversationId != null && messages.length === 0) {
      loadConversation(conversationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  const startNewConversation = useCallback(() => {
    setConversationId(null);
    setMessages([]);
    setInput('');
  }, []);

  const sendMessageText = useCallback(async (text: string) => {
    setInput('');
    setIsLoading(true);

    // crypto.randomUUID() avoids ms-collision when the user sends two messages
    // within a single tick (React key collisions break streaming-delta routing).
    const userMsg: ChatMessage = {
      id: `user-${crypto.randomUUID()}`,
      role: 'user',
      content: text,
      timestamp: new Date(),
      status: 'success',
    };
    const assistantMsg: ChatMessage = {
      id: `assistant-${crypto.randomUUID()}`,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
      status: 'sending',
    };

    setMessages(prev => [...prev, userMsg, assistantMsg]);

    try {
      if (authService.isTokenExpired()) {
        await authService.refreshToken();
      }
      const token = authService.getApiToken();
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${baseUrl}/api/v1/chat/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: text,
          conversation_id: conversationId != null ? Number(conversationId) : undefined,
          company_id: companyIdRef.current ?? undefined,
        }),
      });

      if (!response.ok || !response.body) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        let currentEvent = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            let payload: any = null;
            try {
              payload = JSON.parse(line.slice(6));
            } catch {
              // non-JSON line, skip
            }
            if (payload !== null) {
              if (currentEvent === 'delta' && payload.text) {
                setMessages(prev =>
                  prev.map(m =>
                    m.id === assistantMsg.id
                      ? { ...m, content: m.content + payload.text }
                      : m
                  )
                );
              } else if (currentEvent === 'connecting' && enableToolStatus) {
                setMessages(prev =>
                  prev.map(m =>
                    m.id === assistantMsg.id
                      ? { ...m, toolStatus: { tool: '_connecting', phase: 'calling' } }
                      : m
                  )
                );
                setToolStartTime(Date.now());
              } else if (currentEvent === 'connected' && enableToolStatus) {
                setMessages(prev =>
                  prev.map(m =>
                    m.id === assistantMsg.id ? { ...m, toolStatus: null } : m
                  )
                );
                setToolStartTime(null);
              } else if (currentEvent === 'tool_status' && enableToolStatus) {
                if (payload.phase === 'calling') {
                  setMessages(prev =>
                    prev.map(m =>
                      m.id === assistantMsg.id
                        ? { ...m, toolStatus: { tool: payload.tool, phase: payload.phase } }
                        : m
                    )
                  );
                  setToolStartTime(Date.now());
                } else if (payload.phase === 'complete') {
                  setMessages(prev =>
                    prev.map(m =>
                      m.id === assistantMsg.id ? { ...m, toolStatus: null } : m
                    )
                  );
                  setToolStartTime(null);
                }
              } else if (currentEvent === 'done' && payload.conversation_id) {
                const newId = String(payload.conversation_id);
                setConversationId(payload.conversation_id);
                if (enableConversationList) {
                  await loadConversations();
                }
                onConversationCreatedRef.current?.(newId);
              } else if (currentEvent === 'error') {
                throw new Error(payload.message || 'Stream error');
              }
            }
            currentEvent = '';
          }
        }
      }

      setMessages(prev =>
        prev.map(m =>
          m.id === assistantMsg.id
            ? { ...m, isStreaming: false, toolStatus: null, status: 'success' as const }
            : m
        )
      );
    } catch (err: any) {
      setMessages(prev =>
        prev.map(m =>
          m.id === assistantMsg.id
            ? {
                ...m,
                content: err.message || 'An error occurred',
                isStreaming: false,
                isError: true,
                toolStatus: null,
                status: 'error' as const,
              }
            : m
        )
      );
    } finally {
      setIsLoading(false);
      setToolStartTime(null);
    }
  }, [conversationId, enableToolStatus, enableConversationList, loadConversations]);

  const sendMessage = useCallback(async (overrideText?: string) => {
    const text = (overrideText ?? input).trim();
    if (!text || isLoading) return;
    await sendMessageText(text);
  }, [input, isLoading, sendMessageText]);

  const retryLastMessage = useCallback(async () => {
    if (isLoading) return;
    const lastUserMsg = [...messages].reverse().find(m => m.role === 'user');
    if (!lastUserMsg?.content) return;
    setMessages(prev => prev.filter(m => !m.isError && m.status !== 'error'));
    await sendMessageText(lastUserMsg.content);
  }, [isLoading, messages, sendMessageText]);

  return {
    messages,
    setMessages,
    isLoading,
    input,
    setInput,
    conversationId,
    setConversationId,
    messagesEndRef,
    sendMessage,
    retryLastMessage,
    startNewConversation,
    loadConversation,
    conversations,
    loadingConversations,
    toolElapsed,
    copiedId,
    copyText,
  };
}
